import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../supabaseClient";

const STORAGE_KEY = "hsl.leagueSelection";

const key = (v) => String(v ?? "").trim().toLowerCase();
const usable = (v) => key(v) !== "" && key(v) !== '""';

const divisionNumber = (label) => {
  const m = /^div\s*(\d+)/i.exec(String(label).trim());
  return m ? Number(m[1]) : null;
};

/** DIV 1, DIV 2, … first (numerically), then named divisions such as 35+. */
export const compareDivisions = (a, b) => {
  const na = divisionNumber(a);
  const nb = divisionNumber(b);
  if (na != null && nb != null) return na - nb;
  if (na != null) return -1;
  if (nb != null) return 1;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

const SEASON_TERMS = { F: "Fall", W: "Winter", SP: "Spring", S: "Spring", SU: "Summer" };

/** "F26" → "Fall 2026"; anything unrecognised is shown as stored. */
export function seasonLabel(season) {
  const m = /^([A-Za-z]+)\s*'?(\d{2}|\d{4})$/.exec(String(season ?? "").trim());
  if (!m) return String(season ?? "");
  const term = SEASON_TERMS[m[1].toUpperCase()];
  if (!term) return String(season);
  const year = m[2].length === 2 ? `20${m[2]}` : m[2];
  return `${term} ${year}`;
}

const SEASON_ORDER = { W: 0, SP: 1, S: 1, SU: 2, F: 3 };

/** Newest season first. */
export const compareSeasonsDesc = (a, b) => {
  const parse = (s) => {
    const m = /^([A-Za-z]+)\s*'?(\d{2,4})$/.exec(String(s ?? "").trim());
    if (!m) return [0, -1];
    const y = Number(m[2].length === 2 ? `20${m[2]}` : m[2]);
    return [y, SEASON_ORDER[m[1].toUpperCase()] ?? -1];
  };
  const [ya, ta] = parse(a);
  const [yb, tb] = parse(b);
  return yb - ya || tb - ta || String(b).localeCompare(String(a));
};

/**
 * Groups fixture rows `{ city, division, age_group, season }` into
 * `[{ city, fixtureCount, divisions: [{ division, ageGroup, seasons, fixtureCount }] }]`.
 * Only cities and divisions with at least one fixture are included.
 */
export function buildCatalog(rows) {
  const cities = new Map();
  for (const row of rows || []) {
    if (!usable(row?.city) || !usable(row?.division)) continue;
    const cityName = String(row.city).trim();
    const city = cities.get(key(cityName)) || { city: cityName, fixtureCount: 0, divisions: new Map() };
    city.fixtureCount += 1;
    const divName = String(row.division).trim();
    const div = city.divisions.get(key(divName)) || {
      division: divName,
      ageGroup: row.age_group || null,
      seasons: new Set(),
      fixtureCount: 0,
    };
    div.fixtureCount += 1;
    if (usable(row.season)) div.seasons.add(String(row.season).trim());
    city.divisions.set(key(divName), div);
    cities.set(key(cityName), city);
  }
  return [...cities.values()]
    .map((c) => ({
      city: c.city,
      fixtureCount: c.fixtureCount,
      divisions: [...c.divisions.values()]
        .map((d) => ({ ...d, seasons: [...d.seasons].sort(compareSeasonsDesc) }))
        .sort((a, b) => compareDivisions(a.division, b.division)),
    }))
    .sort((a, b) => a.city.localeCompare(b.city));
}

const findCity = (catalog, city) => catalog.find((c) => key(c.city) === key(city));
const findDivision = (entry, division) =>
  entry?.divisions.find((d) => key(d.division) === key(division));

/**
 * Picks a valid city / division / season. Each level falls back from the requested
 * value, to the remembered one, to the first option (the busiest city for the city level).
 */
export function resolveSelection(catalog, requested = {}, remembered = {}) {
  if (!catalog?.length) return { city: null, division: null, season: null, ageGroup: null };
  const busiest = [...catalog].sort((a, b) => b.fixtureCount - a.fixtureCount)[0];
  const cityEntry = findCity(catalog, requested.city) || findCity(catalog, remembered.city) || busiest;
  const divEntry =
    findDivision(cityEntry, requested.division) ||
    findDivision(cityEntry, remembered.division) ||
    cityEntry.divisions[0];
  const seasons = divEntry?.seasons || [];
  const pick = (s) => seasons.find((x) => key(x) === key(s));
  const season = pick(requested.season) || pick(remembered.season) || seasons[0] || null;
  return {
    city: cityEntry.city,
    division: divEntry?.division ?? null,
    season,
    ageGroup: divEntry?.ageGroup ?? null,
  };
}

export async function fetchLeagueCatalog(sport) {
  const { data, error } = await supabase
    .from("fixtures")
    .select("city, division, age_group, season")
    .eq("sport", sport);
  if (error) throw error;
  return buildCatalog(data);
}

const readRemembered = (sport) => {
  try {
    return JSON.parse(window.localStorage.getItem(`${STORAGE_KEY}.${sport}`) || "{}") || {};
  } catch {
    return {};
  }
};

const remember = (sport, selection) => {
  try {
    window.localStorage.setItem(`${STORAGE_KEY}.${sport}`, JSON.stringify(selection));
  } catch {
    /* storage unavailable (private mode) */
  }
};

/**
 * City / division / season for the league pages, kept in the URL (`city`, `division`,
 * `season`) and remembered between visits. Options come from published fixtures only.
 */
export function useLeagueSelection(sport = "Soccer") {
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setCatalog(null);
    fetchLeagueCatalog(sport)
      .then((c) => !cancelled && setCatalog(c))
      .catch((e) => {
        console.error("League catalog:", e);
        if (!cancelled) {
          setError(e);
          setCatalog([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sport]);

  const requested = {
    city: searchParams.get("city"),
    division: searchParams.get("division"),
    season: searchParams.get("season"),
  };

  const selection = useMemo(
    () => (catalog ? resolveSelection(catalog, requested, readRemembered(sport)) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalog, requested.city, requested.division, requested.season, sport]
  );

  useEffect(() => {
    if (!selection?.city) return;
    remember(sport, { city: selection.city, division: selection.division, season: selection.season });
  }, [selection, sport]);

  const update = (changes) => {
    const next = new URLSearchParams(searchParams);
    const merged = { ...selection, ...changes };
    if ("city" in changes) {
      const entry = findCity(catalog || [], changes.city);
      if (!findDivision(entry, merged.division)) merged.division = entry?.divisions[0]?.division ?? null;
    }
    for (const k of ["city", "division", "season"]) {
      if (merged[k]) next.set(k, merged[k]);
      else next.delete(k);
    }
    next.delete("date");
    next.delete("team");
    setSearchParams(next, { replace: true });
  };

  const cityEntry = selection ? findCity(catalog, selection.city) : null;
  const divEntry = findDivision(cityEntry, selection?.division);

  return {
    loading: catalog === null,
    error,
    catalog: catalog || [],
    ...(selection || { city: null, division: null, season: null, ageGroup: null }),
    divisions: cityEntry?.divisions || [],
    seasons: divEntry?.seasons || [],
    setCity: (city) => update({ city }),
    setDivision: (division) => update({ division }),
    setSeason: (season) => update({ season }),
  };
}
